/**
 * Les gestes de « Ma dispo » et ses deux écritures : enregistrer la semaine (`confirmWeek`, un
 * remplacement de toute la semaine, tamponnée « vérifiée » dans la même requête) et changer la date
 * de démarrage (`updateStartingDate`).
 *
 * Port de `MaDispoScreen.js` :
 *  - le bouton du bas est l'UNIQUE enregistrement : une semaine confirmée ramène au tableau de bord
 *    (`menu`) ; sinon l'engagement se relit (« Confirmer ma dispo »), puis la semaine part ; après
 *    l'enregistrement, le tableau de bord — ou là où visait la sortie que la garde a retenue ;
 *  - quitter avec un brouillon (flèche, retour matériel, toute sortie) ouvre « Enregistrer tes
 *    modifications ? » : « Confirmer ma dispo » enregistre, « Ne pas enregistrer » quitte, fermer reste ;
 *  - « Je ne peux plus assurer ce créneau » (la boîte d'une heure avec live) et le lien du bas ouvrent
 *    « Libérer des créneaux » ;
 *  - un envoi refusé se dit dans une boîte « Erreur », comme l'ancien `useApiStatus`.
 * Mesurés : `availability.confirm_week` (ouvert par la boîte de l'engagement ou de la sortie),
 * `availability.starting_date` (ouvert par sa boîte).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef } from "react"

import { tutorAvailabilityService } from "../../../../../../data/services/tutorAvailabilityService"
import { continuityDuration, slotStartOf } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { type TutorAvailabilityViewModel } from "../../../../view-models/tutor/Availability/tutorAvailabilityViewModel"
import { formatLongDate } from "../../../formatters/dateFormatter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorHour } from "../../../types/tutorDispoOffres"
import {
    initialTutorAvailabilityUiState,
    tutorAvailabilityUiReducer,
    type TutorAvailabilityUiAction,
    type TutorAvailabilityUiState,
} from "./tutorAvailabilityUiState"
import type { TutorAvailabilityDialogProps } from "../../../../../../design-system/templates/TutorAvailabilityDialog"

/** L'écran d'où partent les gestes, en propriété `surface` de leur mesure. */
const SURFACE = "tutor_availability"

const SAVE_FALLBACK = "Ta dispo n'a pas pu être enregistrée. Réessaie dans un instant."

type LeaveAction = { type: string; payload?: object }
type AvailabilityNavigation = {
    navigate: (route: string) => void
    push: (route: string, params?: object) => void
    dispatch: (action: LeaveAction) => void
    addListener: (
        event: "beforeRemove",
        listener: (event: { preventDefault: () => void; data: { action: LeaveAction } }) => void
    ) => () => void
}

/** Le brouillon et la boîte : lus par le view-model, avant les gestes. */
export function useTutorAvailabilityUi() {
    return useReducer(tutorAvailabilityUiReducer, initialTutorAvailabilityUiState)
}

export function useTutorAvailabilityState(
    vm: TutorAvailabilityViewModel,
    ui: TutorAvailabilityUiState,
    dispatchUi: (action: TutorAvailabilityUiAction) => void
) {
    const navigation = useNavigation() as unknown as AvailabilityNavigation
    const goBack = useBackOr("menu")
    // Les deux mots du bouton promettent le tableau de bord : on y va, quel que soit l'écran d'où l'on vient.
    const goToDashboard = useCallback(() => navigation.navigate("menu"), [navigation])

    // Levé juste avant une sortie autorisée, pour que la garde ne retienne pas ce qu'elle vient de
    // permettre ; `pendingLeave` garde la sortie retenue, pour arriver là où le tuteur visait.
    const bypassLeaveGuardRef = useRef(false)
    const pendingLeaveRef = useRef<LeaveAction | null>(null)
    const leaveScreen = useCallback(
        (fallback: () => void = goBack) => {
            bypassLeaveGuardRef.current = true
            const pending = pendingLeaveRef.current
            pendingLeaveRef.current = null
            if (pending) navigation.dispatch(pending)
            else fallback()
        },
        [navigation, goBack]
    )

    const showError = (message: string) => dispatchUi({ type: "SHOW_ERROR", message })
    const confirmWeek = useApiMutation({
        service: tutorAvailabilityService.confirmWeek,
        gesture: "availability.confirm_week",
        surface: SURFACE,
        fallback: SAVE_FALLBACK,
        skipWhilePending: true,
        onSuccess: () => {
            dispatchUi({ type: "RESEED" })
            leaveScreen(goToDashboard)
        },
        onError: showError,
    })
    const startingDate = useApiMutation({
        service: tutorAvailabilityService.updateStartingDate,
        gesture: "availability.starting_date",
        surface: SURFACE,
        fallback: SAVE_FALLBACK,
        skipWhilePending: true,
        onError: showError,
    })

    const { dirty } = vm
    useEffect(
        () =>
            navigation.addListener("beforeRemove", (event) => {
                if (!dirty || bypassLeaveGuardRef.current) return
                event.preventDefault()
                pendingLeaveRef.current = event.data.action
                confirmWeek.gesture.opened()
                dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "leave" } })
            }),
        [navigation, dirty, confirmWeek.gesture, dispatchUi]
    )

    const closeDialog = () => dispatchUi({ type: "CLOSE_DIALOG" })
    const saveWeek = () => {
        dispatchUi({ type: "CLOSE_DIALOG" })
        if (!vm.canSave) return
        confirmWeek.start({}, { slots: vm.openSlots.map(slotStartOf) })
    }
    const openRelease = (slot?: string) => navigation.push("tutor/liberer-creneaux", slot ? { slot } : undefined)

    const dialog = ui.dialog
    let dialogProps: TutorAvailabilityDialogProps | null = null
    if (dialog?.kind === "startingDate") {
        dialogProps = {
            visible: true,
            onClose: closeDialog,
            kind: "startingDate",
            dateLabel: formatLongDate(dialog.date),
            onConfirm: () => {
                closeDialog()
                startingDate.start({}, { availability_starting_date: dialog.date })
            },
            saving: startingDate.requesting,
        }
    } else if (dialog?.kind === "continuity") {
        dialogProps = {
            visible: true,
            onClose: closeDialog,
            kind: "continuity",
            mode: dialog.mode,
            duration: continuityDuration(),
            // L'accusé vient APRÈS l'heure posée : un seul « J'ai compris », rien qui ferait croire à un retour.
            onConfirm: dialog.mode === "confirm" ? saveWeek : undefined,
            saving: confirmWeek.requesting,
        }
    } else if (dialog?.kind === "leave") {
        dialogProps = {
            visible: true,
            // Fermer reste ici : la sortie retenue tombe avec la boîte.
            onClose: () => {
                pendingLeaveRef.current = null
                closeDialog()
            },
            kind: "leave",
            onSave: saveWeek,
            onDiscard: () => {
                closeDialog()
                dispatchUi({ type: "RESEED" })
                leaveScreen()
            },
            saving: confirmWeek.requesting,
        }
    } else if (dialog?.kind === "live") {
        dialogProps = {
            visible: true,
            onClose: closeDialog,
            kind: "live",
            liveTimesLabel: dialog.liveTimesLabel,
            onRelease: () => {
                closeDialog()
                openRelease(dialog.slotId)
            },
        }
    }

    return {
        onBack: goBack,
        onPickStartingDate: (date: string) => {
            // La boîte ne s'ouvre que pour une autre date : sa mesure s'ouvre avec elle.
            if (date && date !== vm.startingDate) startingDate.gesture.opened()
            dispatchUi({ type: "PICK_STARTING_DATE", date, current: vm.startingDate })
        },
        onPressHour: (hour: UITutorHour) =>
            dispatchUi({ type: "PRESS_HOUR", hour, serverOpenSlots: vm.serverOpenSlots }),
        onRelease: () => openRelease(),
        onConfirm: () => {
            if (!vm.canSave) return
            if (vm.freshness === "verified") {
                goToDashboard()
                return
            }
            confirmWeek.gesture.opened()
            dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "continuity", mode: "confirm" } })
        },
        saving: confirmWeek.requesting,
        dialog: dialogProps,
        errorMessage: ui.errorMessage,
        onDismissError: () => dispatchUi({ type: "DISMISS_ERROR" }),
    }
}
